import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import {
  history,
  Link,
  RequestConfig,
  type RunTimeLayoutConfig,
} from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
  SelectTimezone,
} from '@/components';
import CustomTabsLayout from '@/components/CustomTabsLayout';
import TimezoneProvider from '@/components/TimezoneProvider';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { normalizeTabPath } from '@/utils/tabTitle';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/** 自定义多 Tab：标题随 locale 响应式更新，避免刷新后显示英文 route name */
export const getCustomTabs = () => CustomTabsLayout;

/** 统一路径格式，避免 /path 与 /path/ 被 keepalive 当成两个 Tab */
export function onRouteChange({
  location,
}: {
  location: { pathname: string; search?: string; hash?: string };
}) {
  const normalized = normalizeTabPath(location.pathname);
  if (location.pathname !== normalized) {
    history.replace(`${normalized}${location.search || ''}${location.hash || ''}`);
  }
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async (): Promise<API.CurrentUser | undefined> => {
    // 检查是否有 token
    const token = localStorage.getItem('corebtc_crm_token');
    if (!token) {
      return undefined;
    }

    try {
      // 调用后端 API 获取当前用户信息
      const result = await queryCurrentUser({ skipErrorHandler: true });
      if (result.success && result.data?.user) {
        const user = result.data.user;
        return {
          name: user.username,
          avatar:
            user.avatar ||
            `https://api.dicebear.com/9.x/notionists/svg?seed=${user.username}`,
          userid: user._id || user.id,
          uid: user.uid,
          email: user.email,
          access: 'admin',
          permissions: user.permissions || [],
          isSuperAdmin: user.isSuperAdmin || false,
          roleName: user.role?.name,
        };
      }
    } catch (error) {
      // Token 无效或过期，清除本地存储
      localStorage.removeItem('corebtc_crm_token');
      localStorage.removeItem('corebtc_crm_user');
    }
    return undefined;
  };

  // 如果不是登录页面，获取用户信息
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      history.push(loginPath);
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <SelectTimezone key="SelectTimezone" />,
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menuItemRender: (menuItemProps, defaultDom) => {
      if (menuItemProps.isUrl || menuItemProps.children || !menuItemProps.path) {
        return defaultDom;
      }

      const targetPath = normalizeTabPath(
        menuItemProps.path.replace('/*', ''),
      );

      if (normalizeTabPath(history.location.pathname) !== targetPath) {
        return (
          <Link to={targetPath} target={menuItemProps.target}>
            {defaultDom}
          </Link>
        );
      }

      return defaultDom;
    },
    childrenRender: (children) => {
      return (
        <TimezoneProvider>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </TimezoneProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 运行时配置
 * @description 配置请求拦截器、响应拦截器、错误处理等
 */
export const request = {
  ...errorConfig,
};
